<!-- Content Wrapper Start -->
<div class="fm-content-wrapper">
  <div class="container-fluid">

    <!-- ═══════════════════════════════════════════════════════════════════
         State 1 — API / network error
         ═══════════════════════════════════════════════════════════════════ -->
    @if (hasError()) {
      <div class="fm-white-column text-center py-5">
        <h4 class="fm-head-24 fm-head-sty2">Something went wrong</h4>
        <p class="mb-3">We couldn't load this story. Please check your connection and try again.</p>
        <button type="button" class="fm-common-tbtn" (click)="loadStory()">
          Try Again <img class="fm-svg" src="images/icons/fm-arrow-ar.svg" alt="Retry" />
        </button>
      </div>
    }

    <!-- ═══════════════════════════════════════════════════════════════════
         State 2 — Story loaded successfully
         ═══════════════════════════════════════════════════════════════════ -->
    @else if (story()) {

      <!-- Back navigation -->
      <button type="button" class="fm-back-btn mb-4" (click)="goBack()">
        <i class="bi bi-arrow-left me-2"></i>Back to Stories
      </button>

      <!-- Stories Details Content Start -->
      <div class="row g-4">

        <!-- ═══════════════════════════════════════════════════════════════
             LEFT COLUMN — media player + transcript
             ═══════════════════════════════════════════════════════════════ -->
        <div class="col-md-7 col-xl-8">
            <div class="fm-white-column mt-3">
          <!-- ── Video Player — full column width, outside the white-card padding ── -->
          <!-- NOT using fm-story-vplayer — global CSS applies blur overlay via
               .fm-stories-blog .fm-story-vplayer:after -->
          <div class="fm-detail-player">

            <!-- Vimeo ready — SDK-powered player matching Response component -->
            @if (isVimeoReady && vimeoEmbedUrl) {
              <div class="fm-vimeo-wrapper" #vimeoWrapper [class.is-playing]="isVimeoPlaying()">
                <iframe
                  #vimeoIframe
                  class="fm-vimeo-iframe"
                  [src]="vimeoEmbedUrl"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowfullscreen
                  frameborder="0"
                  [title]="story()!.title"
                ></iframe>

                <!-- Center play overlay — click to start / resume -->
                @if (!isVimeoPlaying()) {
                  <div class="fm-vimeo-overlay" (click)="toggleVimeoPlay()">
                    <div class="fm-vimeo-play-btn">
                      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="white" viewBox="0 0 16 16">
                        <path d="M10.804 8 5 4.633v6.734zm.792-.696a.802.802 0 0 1 0 1.392l-6.363 3.692C4.713 12.69 4 12.345 4 11.692V4.308c0-.653.713-.998 1.233-.696z"/>
                      </svg>
                    </div>
                  </div>
                }

                <!-- Bottom controls: play/pause, time, seek, fullscreen -->
                <div class="fm-vimeo-controls">
                  <button class="fm-vimeo-ctrl-btn" (click)="toggleVimeoPlay()"
                    [title]="isVimeoPlaying() ? 'Pause' : 'Play'">
                    @if (isVimeoPlaying()) {
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M6 3.5a.5.5 0 0 1 .5.5v8a.5.5 0 0 1-1 0V4a.5.5 0 0 1 .5-.5m4 0a.5.5 0 0 1 .5.5v8a.5.5 0 0 1-1 0V4a.5.5 0 0 1 .5-.5"/>
                      </svg>
                    } @else {
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                        <path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393"/>
                      </svg>
                    }
                  </button>
                  <span class="fm-vimeo-time">{{ vimeoCurrentTime() }} / {{ vimeoTotalTime() }}</span>
                  <input
                    #seekBar
                    class="fm-vimeo-seek"
                    type="range"
                    min="0"
                    [max]="vimeoDuration()"
                    [value]="vimeoProgress()"
                    (input)="vimeoSeek($event)"
                  />
                  <button class="fm-vimeo-ctrl-btn" (click)="requestVimeoFullscreen()" title="Fullscreen">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M1.5 1h4a.5.5 0 0 1 0 1H2v3.5a.5.5 0 0 1-1 0v-4A.5.5 0 0 1 1.5 1zm9 0h4a.5.5 0 0 1 .5.5v4a.5.5 0 0 1-1 0V2h-3.5a.5.5 0 0 1 0-1zM1 10.5a.5.5 0 0 1 .5-.5h3.5v-3.5a.5.5 0 0 1 1 0V11a.5.5 0 0 1-.5.5h-4a.5.5 0 0 1-.5-.5zm9 0V7a.5.5 0 0 1 1 0v3.5H15a.5.5 0 0 1 0 1h-4a.5.5 0 0 1-.5-.5z"/>
                    </svg>
                  </button>
                </div>
              </div>
            }

            <!-- Native video (no Vimeo) -->
            @else if (story()!.mediaUrl) {
              <video
                class="fm-story-video"
                [src]="story()!.mediaUrl"
                [poster]="story()!.vimeoThumbnailUrl || ''"
                controls
                playsinline
                disablepictureinpicture
                disableremoteplayback
                controlslist="nodownload noplaybackrate"
              ></video>
            }

            <!-- No media available -->
            @else {
              <div class="fm-video-empty-state">
                <img src="images/icons/fm-video-lg.svg" class="fm-svg" alt="No Media" />
                <p class="mb-0 mt-2">Media not available for this story.</p>
              </div>
            }

          </div><!-- /fm-detail-player -->

          <!-- ── Card 1: Story metadata ──────────────────────────────── -->
            <div class="fm-stories-blog fm-stories-details">
              <div class="fm-stories-content">
                <div class="row">
                  <div class="col-md-6">
                  <!-- Story info -->
                  <div class="fm-st-promt-tag">
                    <span class="fm-st-ti">{{ story()!.categoryName }}</span>
                    <p class="fw-light mb-0">{{ formatDuration(story()!.duration) }}, {{ getRelativeTime(story()!.createdOn) }}</p>
                  </div>
                  <h4 class="fm-head-20 fm-head-sty2 mb-1">{{ story()!.title }}</h4>
                  <p class="fw-light mb-2">by {{ story()!.uploadedBy }}</p>
                  
                  </div>
                  <div class="col-md-6">
<!-- Reaction buttons -->
                  <p class="fm-black-cl fm-fw-medium mb-2">Reaction</p>
                  <div class="fm-react-btns" [class.fm-reacting]="isReacting()">
                    @for (rt of reactionTypes(); track rt.id) {
                      <div class="fm-react-item">
                        <button
                          type="button"
                          class="fm-react-btn"
                          [class.active]="currentUserReaction() === rt.id"
                          (click)="toggleReaction(rt.id)"
                          [disabled]="isReacting()"
                          [attr.aria-label]="rt.name + ' reaction'"
                        >
                          @if (isReacting() && pendingReactionType() === rt.id) {
                            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          } @else {
                            <img [src]="getReactionIcon(rt.name)" class="fm-svg" [alt]="rt.name" />
                          }
                          <span class="fm-react-text">{{ rt.name }} {{ formatReactionCount(rt.id) }}</span>
                          @if (getReactionUsers(rt.id).length > 0) {
                            <div class="fm-react-hover-list">
                              <ul class="fm-react-list-items">
                                @for (r of getReactionUsers(rt.id); track r.reactionId) {
                                  <li class="fm-react-user">
                                    <span class="fm-react-user-avatar">
                                      @if (r.userProfilePicUrl) {
                                        <img [src]="r.userProfilePicUrl" [alt]="r.userName" />
                                      } @else {
                                        {{ getReactionUserInitials(r.userName) }}
                                      }
                                    </span>
                                    <span class="fm-react-user-name">{{ r.userName }}</span>
                                  </li>
                                }
                              </ul>
                            </div>
                          }
                        </button>
                      </div>
                    }
                  </div><!-- /fm-react-btns -->

                  </div>
                </div>
                  
              </div><!-- /fm-stories-content -->

            </div><!-- /fm-stories-blog -->
          </div><!-- /fm-white-column card 1 -->

          <!-- ── Card 2 (Story owner): Auto-Generated Transcript — 2 tabs, editable ── -->
          @if (story()!.isOwnStory) {
            <div class="fm-white-column mt-4">
              <div class="accordion fm-accordion fm-accordion-st2" id="fmTranscriptAccordion">
                <div class="accordion-item fm-accordion-item mt-0">

                  <span class="accordion-header fm-accordion-header fm-border-bottom">
                    <button
                      class="accordion-button fm-accordion-button fm-accordion-action"
                      [class.collapsed]="!transcriptOpen()"
                      type="button"
                      (click)="toggleTranscript()"
                      [attr.aria-expanded]="transcriptOpen()"
                      aria-controls="fmTranscriptCollapse"
                    >
                      <span class="fm-acc-title">Auto-Generated Transcript</span>

                      <!-- Edit button — visible when not editing -->
                      @if (!isEditingTranscript()) {
                        <span
                          class="fm-edit-btn fm-transcript-edit-btn"
                          role="button"
                          (click)="$event.stopPropagation(); startTranscriptEdit()"
                        >
                          <img src="images/icons/fm-edit.svg" class="fm-svg" alt="Edit Icon" /> Edit
                        </span>
                      }

                      <!-- Save button — visible when editing -->
                      @if (isEditingTranscript()) {
                        <span
                          class="fm-edit-btn fm-transcript-save-btn"
                          role="button"
                          [class.disabled]="isSavingTranscript()"
                          (click)="$event.stopPropagation(); saveTranscript()"
                        >
                          @if (isSavingTranscript()) {
                            <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                          } @else {
                            <img src="images/icons/fm-check-sm.svg" class="fm-svg" alt="Save Icon" />
                          }
                          Save
                        </span>
                      }

                    </button>
                  </span>

                  <div
                    id="fmTranscriptCollapse"
                    class="accordion-collapse fm-accordion-collapse collapse"
                    [class.show]="transcriptOpen()"
                  >
                    <div class="accordion-body fm-accordion-body pt-2">

                      <!-- Verbatim | Narrative tab bar -->
                      <div class="fm-tabbar fm-tabbar-st2">
                        <ul class="nav nav-tabs fm-nav-tabs fm-nav-tabs-st2" role="tablist">
                          <li class="fm-nav-item" role="presentation">
                            <button
                              class="fm-nav-link"
                              [class.active]="activeTranscriptTab() === 1"
                              type="button"
                              role="tab"
                              (click)="setTranscriptTab(1)"
                            >Verbatim</button>
                          </li>
                          <li class="fm-nav-item" role="presentation">
                            <button
                              class="fm-nav-link"
                              [class.active]="activeTranscriptTab() === 2"
                              type="button"
                              role="tab"
                              (click)="setTranscriptTab(2)"
                            >Narrative</button>
                          </li>
                        </ul>
                      </div>

                      <!-- Tab panes -->
                      <div class="tab-content mt-3">
                        @if (activeTranscriptTab() === 1) {
                          <div class="tab-pane fade show active">
                            @if (!verbatimDraft().trim() && !isEditingTranscript()) {
                              <p class="text-muted fst-italic mb-0">No transcript available for this story.</p>
                            } @else {
                              <div class="form-group">
                                <textarea
                                  class="form-control fm-transcript-textarea"
                                  rows="12"
                                  [attr.readonly]="!isEditingTranscript() || null"
                                  [value]="verbatimDraft()"
                                  (input)="verbatimDraft.set($any($event.target).value)"
                                ></textarea>
                              </div>
                            }
                          </div>
                        } @else {
                          <div class="tab-pane fade show active">
                            @if (!narrativeDraft().trim() && !isEditingTranscript()) {
                              <p class="text-muted fst-italic mb-0">No transcript available for this story.</p>
                            } @else {
                              <div class="form-group">
                                <textarea
                                  class="form-control fm-transcript-textarea"
                                  rows="12"
                                  [attr.readonly]="!isEditingTranscript() || null"
                                  [value]="narrativeDraft()"
                                  (input)="narrativeDraft.set($any($event.target).value)"
                                ></textarea>
                              </div>
                            }
                          </div>
                        }
                      </div>

                    </div>
                  </div>

                </div>
              </div>
              <p class="mt-3 mb-0 fm-light fm-small">AI-generated transcript. Click to edit any mistakes.</p>
            </div>
          }

          <!-- ── Card 2 (Viewer): Full Transcript — read-only ── -->
          @else {
            <div class="fm-white-column mt-4">
              <div class="accordion fm-accordion fm-accordion-st2" id="fmTranscriptAccordion">
                <div class="accordion-item fm-accordion-item mt-0">

                  <h2 class="accordion-header fm-accordion-header">
                    <button
                      class="accordion-button fm-accordion-button"
                      [class.collapsed]="!transcriptOpen()"
                      type="button"
                      (click)="toggleTranscript()"
                      [attr.aria-expanded]="transcriptOpen()"
                      aria-controls="fmFullTranscriptCollapse"
                    >
                      Full Transcript
                    </button>
                  </h2>

                  <div
                    id="fmFullTranscriptCollapse"
                    class="accordion-collapse fm-accordion-collapse collapse"
                    [class.show]="transcriptOpen()"
                  >
                    <div class="accordion-body fm-accordion-body">
                      <div class="fm-border-column">
                        @if (getTranscriptText()) {
                          <p class="fm-dark fm-transcript-text">{{ getTranscriptText() }}</p>
                        } @else {
                          <p class="text-muted fst-italic mb-0">No transcript available for this story.</p>
                        }
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          }<!-- /fm-white-column card 2 -->

        </div><!-- /col-md-7 (left column) -->

        <!-- ═══════════════════════════════════════════════════════════════
             RIGHT SIDEBAR — actions / story details / related stories
             ═══════════════════════════════════════════════════════════════ -->
        <div class="col-md-5 col-xl-4">

          <!-- ── Sidebar 1: Action buttons (Share / Download / Report) ── -->
          <div class="fm-sidebar-clumn">
            <div class="fm-border-column fm-color-brd">
              <div class="row g-3">
                <div class="col-12">
                  <!-- Share Story — visible to story owner only -->
                  @if (story()!.isOwnStory) {
                    <button
                      type="button"
                      class="fm-common-btn fm-large-btn w-100 mt-0"
                      (click)="openShareModal()"
                    >
                      <img class="fm-svg" src="images/icons/fm-share.svg" alt="Share Icon" />
                      Share Story
                    </button>
                  }
                </div>
                <div class="col-12">
                  <!-- Download — Vimeo-ready: calls API; native mediaUrl: direct download -->
                  <button
                    type="button"
                    class="fm-common-btn fm-secondary-btn fm-large-btn w-100 mt-1"
                    [disabled]="isDownloading()"
                    (click)="downloadVideo()"
                  >
                    @if (isDownloading()) {
                      <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                    } @else {
                      <img class="fm-svg" src="images/icons/fm-download.svg" alt="Download Icon" />
                    }
                    Download
                  </button>
                </div>
                <div class="col-12">
                  <!-- Report Story — hidden for story owner -->
                  @if (!story()!.isOwnStory) {
                    <button type="button" class="fm-common-btn fm-border-btn fm-large-btn w-100 mt-1"
                      (click)="openReportModal()">
                      <img class="fm-svg" src="images/icons/fm-flag.svg" alt="Flag Icon" /> Report Story
                    </button>
                  }
                </div>
              </div>
            </div>
          </div><!-- /fm-sidebar-clumn actions -->

          <!-- ── Sidebar 2: Story Details ────────────────────────────── -->
          <div class="fm-sidebar-clumn">
            <div class="fm-border-column fm-color-brd">
              <div class="row">
                <div class="col-12">
                  <h3 class="fm-head-20 fm-sidebar-title">Story Details</h3>
                </div>
                <div class="col-12">
                  <div class="fm-story-details-grid">

                    <!-- Date — always visible; shows '—' when API returns no date -->
                    <div class="fm-detail-item">
                      <div class="fm-detail-icon">
                        <img class="fm-svg" src="images/icons/fm-calendar.svg" alt="Date Icon" />
                      </div>
                      <div class="fm-detail-content">
                        <h4 class="fm-detail-heading">Date</h4>
                        <p class="fm-detail-text">{{ story()!.storyDate ? formatStoryDate(story()!.storyDate) : '—' }}</p>
                      </div>
                    </div>

                    <!-- Location — hidden when not provided -->
                    @if (story()!.location) {
                      <div class="fm-detail-item">
                        <div class="fm-detail-icon">
                          <img class="fm-svg" src="images/icons/fm-location.svg" alt="Location Icon" />
                        </div>
                        <div class="fm-detail-content">
                          <h4 class="fm-detail-heading">Location</h4>
                          <p class="fm-detail-text">{{ story()!.location }}</p>
                        </div>
                      </div>
                    }

                    <!-- Tagged People — view mode / edit mode (owner only) -->
                    <div class="fm-detail-item">
                      <div class="fm-detail-icon">
                        <img class="fm-svg" src="images/icons/fm-people-grp.svg" alt="People Icon" />
                      </div>
                      <div class="fm-detail-content">

                        <!-- Heading row: label + edit pencil (owner, view mode only) -->
                        <div class="fm-detail-heading-row">
                          <h4 class="fm-detail-heading">People tagged</h4>
                          @if (story()!.isOwnStory && !isEditingTaggedWith()) {
                            <button type="button" class="fm-edit-inline-btn"
                              (click)="startEditTaggedWith()" title="Edit">
                              <img class="fm-svg" src="images/icons/fm-edit.svg" alt="Edit" />
                            </button>
                          }
                        </div>

                        <!-- View mode -->
                        @if (!isEditingTaggedWith()) {
                          <p class="fm-detail-text">
                            {{ (story()!.taggedPeople?.length ?? 0) > 0 ? getTaggedPeopleNames() : '—' }}
                          </p>
                        }

                        <!-- Edit mode: Kendo-style multiselect -->
                        @else {
                          <div class="fm-kms-wrapper">

                            <!-- ── Trigger: chips + search input + chevron ── -->
                            <div class="fm-kms-control">
                              @for (id of taggedWithDraft(); track id) {
                                <span class="fm-kms-tag">
                                  {{ getMemberName(id) }}
                                  <button type="button" class="fm-kms-tag-remove"
                                    (click)="$event.stopPropagation(); toggleTaggedMember(id)"
                                    aria-label="Remove">&#x2715;</button>
                                </span>
                              }
                              <input type="text" class="fm-kms-input"
                                [(ngModel)]="memberTagSearchText"
                                [placeholder]="taggedWithDraft().length === 0 ? 'Select people…' : ''"
                                autocomplete="off" />
                              <span class="fm-kms-arrow" aria-hidden="true">
                                <svg viewBox="0 0 10 6" width="10" height="6">
                                  <polyline points="1,1 5,5 9,1" stroke-width="1.5"
                                    stroke="currentColor" fill="none" stroke-linecap="round"/>
                                </svg>
                              </span>
                            </div>

                            <!-- ── Floating dropdown list ── -->
                            <div class="fm-kms-dropdown">
                              @if (isLoadingMembers()) {
                                <p class="fm-kms-empty">Loading…</p>
                              } @else if (filteredTagMembers.length === 0) {
                                <p class="fm-kms-empty">No members found.</p>
                              } @else {
                                <ul class="fm-kms-list">
                                  @for (m of filteredTagMembers; track m.familyMemberId) {
                                    <li class="fm-kms-item"
                                      [class.fm-kms-item--selected]="isTaggedMemberSelected(m.familyMemberId)"
                                      (click)="toggleTaggedMember(m.familyMemberId)">
                                      <span class="fm-kms-check"
                                        [class.fm-kms-check--on]="isTaggedMemberSelected(m.familyMemberId)">
                                        <svg viewBox="0 0 12 12" aria-hidden="true">
                                          <polyline points="2,6 5,9 10,3" stroke-width="2.2" fill="none"/>
                                        </svg>
                                      </span>
                                      <span class="fm-kms-name">{{ m.fullName }}</span>
                                      <span class="fm-kms-rel">{{ m.relationship }}</span>
                                    </li>
                                  }
                                </ul>
                              }
                              <!-- Save / Cancel -->
                              <div class="fm-kms-actions">
                                <button type="button" class="fm-kms-cancel-btn"
                                  (click)="cancelEditTaggedWith()">Cancel</button>
                                <button type="button" class="fm-kms-save-btn"
                                  (click)="saveTaggedWith()"
                                  [disabled]="isSavingTaggedWith()">
                                  @if (isSavingTaggedWith()) { Saving… } @else { Save }
                                </button>
                              </div>
                            </div><!-- /fm-kms-dropdown -->

                          </div><!-- /fm-kms-wrapper -->
                        }

                      </div>
                    </div>

                    <!-- Shared With — view mode / edit mode (owner only) -->
                    <div class="fm-detail-item">
                      <div class="fm-detail-icon">
                        <img class="fm-svg" src="images/icons/fm-people-grp.svg" alt="Shared With Icon" />
                      </div>
                      <div class="fm-detail-content">

                        <!-- Heading row: label + edit pencil (owner, view mode only) -->
                        <div class="fm-detail-heading-row">
                          <h4 class="fm-detail-heading">People shared</h4>
                          @if (story()!.isOwnStory && !isEditingSharedWith()) {
                            <button type="button" class="fm-edit-inline-btn"
                              (click)="startEditSharedWith()" title="Edit">
                              <img class="fm-svg" src="images/icons/fm-edit.svg" alt="Edit" />
                            </button>
                          }
                        </div>

                        <!-- View mode -->
                        @if (!isEditingSharedWith()) {
                          <p class="fm-detail-text">
                            {{ (story()!.sharedWithPeople?.length ?? 0) > 0 ? getSharedWithPeopleNames() : '\u2014' }}
                          </p>
                        }

                        <!-- Edit mode: Kendo-style multiselect -->
                        @else {
                          <div class="fm-kms-wrapper">

                            <!-- ── Trigger: chips + search input + chevron ── -->
                            <div class="fm-kms-control">
                              @for (id of sharedWithDraft(); track id) {
                                <span class="fm-kms-tag">
                                  {{ getMemberName(id) }}
                                  <button type="button" class="fm-kms-tag-remove"
                                    (click)="$event.stopPropagation(); toggleSharedMember(id)"
                                    aria-label="Remove">&#x2715;</button>
                                </span>
                              }
                              <input type="text" class="fm-kms-input"
                                [(ngModel)]="memberSearchText"
                                [placeholder]="sharedWithDraft().length === 0 ? 'Select people…' : ''"
                                autocomplete="off" />
                              <span class="fm-kms-arrow" aria-hidden="true">
                                <svg viewBox="0 0 10 6" width="10" height="6">
                                  <polyline points="1,1 5,5 9,1" stroke-width="1.5"
                                    stroke="currentColor" fill="none" stroke-linecap="round"/>
                                </svg>
                              </span>
                            </div>

                            <!-- ── Floating dropdown list ── -->
                            <div class="fm-kms-dropdown">
                              @if (isLoadingMembers()) {
                                <p class="fm-kms-empty">Loading…</p>
                              } @else if (filteredMembers.length === 0) {
                                <p class="fm-kms-empty">No members found.</p>
                              } @else {
                                <ul class="fm-kms-list">
                                  @for (m of filteredMembers; track m.familyMemberId) {
                                    <li class="fm-kms-item"
                                      [class.fm-kms-item--selected]="isMemberSelected(m.familyMemberId)"
                                      (click)="toggleSharedMember(m.familyMemberId)">
                                      <span class="fm-kms-check"
                                        [class.fm-kms-check--on]="isMemberSelected(m.familyMemberId)">
                                        <svg viewBox="0 0 12 12" aria-hidden="true">
                                          <polyline points="2,6 5,9 10,3" stroke-width="2.2" fill="none"/>
                                        </svg>
                                      </span>
                                      <span class="fm-kms-name">{{ m.fullName }}</span>
                                      <span class="fm-kms-rel">{{ m.relationship }}</span>
                                    </li>
                                  }
                                </ul>
                              }
                              <!-- Save / Cancel -->
                              <div class="fm-kms-actions">
                                <button type="button" class="fm-kms-cancel-btn"
                                  (click)="cancelEditSharedWith()">Cancel</button>
                                <button type="button" class="fm-kms-save-btn"
                                  (click)="saveSharedWith()"
                                  [disabled]="isSavingSharedWith()">
                                  @if (isSavingSharedWith()) { Saving… } @else { Save }
                                </button>
                              </div>
                            </div><!-- /fm-kms-dropdown -->

                          </div><!-- /fm-kms-wrapper -->
                        }

                      </div>
                    </div>

                    <!-- Emotional Tone — hidden when not provided -->
                    @if (story()!.emotionalTone) {
                      <div class="fm-detail-item">
                        <div class="fm-detail-icon">
                          <img class="fm-svg" src="images/icons/fm-heart-pr.svg" alt="Emotional Tone Icon" />
                        </div>
                        <div class="fm-detail-content">
                          <h4 class="fm-detail-heading">Emotional Tone</h4>
                          <p class="fm-detail-text">{{ story()!.emotionalTone }}</p>
                        </div>
                      </div>
                    }

                  </div><!-- /fm-story-details-grid -->
                </div>
              </div>
            </div>
          </div><!-- /fm-sidebar-clumn story details -->

          <!-- ── Sidebar 3: Related Stories ──────────────────────────────
               Shows up to 2 most recent stories in the same category.
               Hidden entirely when list is empty.
               Each card is fully clickable and navigates to that story.
               ─────────────────────────────────────────────────────── -->
          @if ((story()!.relatedStories?.length ?? 0) > 0) {
            <div class="fm-sidebar-clumn">
              <div class="fm-border-column fm-color-brd">
                <div class="row">
                  <div class="col-12">
                    <h3 class="fm-head-20 fm-sidebar-title">Related Stories</h3>
                  </div>
                  <div class="col-12">
                    <div class="fm-related-stories-list">

                      @for (related of story()!.relatedStories; track related.storyId) {
                        <!-- Clickable card — routerLink navigates to story detail -->
                        <a
                          [routerLink]="['/app/stories', related.storyId]"
                          class="fm-related-story-card"
                          [attr.aria-label]="'View story: ' + related.title"
                        >
                          <!-- Thumbnail with duration badge overlay -->
                          <div class="fm-related-thumb">
                            <img
                              class="fm-related-cover"
                              [src]="related.vimeoThumbnailUrl || related.thumbnailUrl || 'images/stories/stories-post-1.png'"
                              [alt]="related.title"
                              onerror="this.src='images/stories/stories-post-1.png'"
                            />
                            <span class="fm-tag fm-related-duration-tag">
                              {{ formatDuration(related.duration) }}
                            </span>
                          </div>

                          <!-- Story info block -->
                          <div class="fm-related-info">
                            <h4 class="fm-related-story-title">{{ related.title }}</h4>
                            <div class="fm-related-meta-row">
                              <!-- Category name shared with parent story -->
                              <span class="fm-related-story-meta">{{ story()!.categoryName }}</span>
                              <!-- View count -->
                              <span class="fm-related-stat">
                                <img class="fm-svg" src="images/icons/fm-eye.svg" alt="Views" />
                                {{ related.viewCount }}
                              </span>
                            </div>
                            <!-- Relative upload date -->
                            <p class="fm-related-date">{{ getRelativeTime(related.createdOn) }}</p>
                          </div>
                        </a>
                      }

                    </div>
                  </div>
                </div>
              </div>
            </div>
          }<!-- /related stories -->

        </div><!-- /col-md-5 (right sidebar) -->

      </div><!-- /row (stories details) -->
      <!-- Stories Details Content End -->

    }<!-- /@else if story() -->

    <!-- ═══════════════════════════════════════════════════════════════════
         Share Story Modal (owner only) — shown when shareModalOpen() is true
         ═══════════════════════════════════════════════════════════════════ -->
    @if (shareModalOpen()) {
      <!-- Backdrop -->
      <div class="fm-modal-backdrop" (click)="closeShareModal()"></div>

      <!-- Dialog -->
      <div class="fm-modal fm-share-modal" role="dialog" aria-modal="true" aria-labelledby="shareModalTitle">
        <div class="fm-modal-header">
          <h5 class="fm-modal-title" id="shareModalTitle">
            <img class="fm-svg me-2" src="images/icons/fm-share.svg" alt="" /> Share Story
          </h5>
          <button type="button" class="fm-modal-close" (click)="closeShareModal()" aria-label="Close">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <div class="fm-modal-body">
          <p class="fm-modal-desc">Copy the link below to share this story with others.</p>

          <!-- URL display + copy row -->
          <div class="fm-share-link-row">
            <div class="fm-share-link-box">
              <span class="fm-share-link-text">{{ shareUrl() }}</span>
            </div>
            <button
              type="button"
              class="fm-common-btn fm-share-copy-btn"
              [class.fm-share-copied]="shareCopied()"
              (click)="copyShareLink()"
            >
              @if (shareCopied()) {
                <i class="bi bi-check2 me-1"></i> Copied!
              } @else {
                <i class="bi bi-clipboard me-1"></i> Copy
              }
            </button>
          </div>
        </div>
      </div>
    }<!-- /share modal -->

    <!-- ═══════════════════════════════════════════════════════════════════════
         Report Story Modal — shown when reportModalOpen() is true
         ═══════════════════════════════════════════════════════════════════════ -->
    @if (reportModalOpen()) {
      <!-- Backdrop -->
      <div class="fm-modal-backdrop" (click)="closeReportModal()"></div>

      <!-- Dialog -->
      <div class="fm-modal fm-report-modal" role="dialog" aria-modal="true" aria-labelledby="reportModalTitle">
        <div class="fm-modal-header">
          <h5 class="fm-modal-title" id="reportModalTitle">
            <img class="fm-svg me-2" src="images/icons/fm-flag.svg" alt="" /> Report Story
          </h5>
          <button type="button" class="fm-modal-close" (click)="closeReportModal()" aria-label="Close">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <div class="fm-modal-body">
          <!-- ── Form ─────────────────────────────────────────────────── -->
          <p class="fm-modal-desc">Select at least one reason for reporting this story. Additional details are optional.</p>

            @if (isLoadingReportReasons()) {
              <div class="text-center py-3">
                <span class="spinner-border spinner-border-sm" style="color: #B84F18" role="status" aria-hidden="true"></span>
              </div>
            } @else {
              <ul class="fm-report-reason-list">
                @for (reason of reportReasons(); track reason.id) {
                  <li
                    class="fm-report-reason-item"
                    [class.fm-report-reason-item--selected]="isReasonSelected(reason.id)"
                    (click)="toggleReportReason(reason.id)"
                  >
                    <span class="fm-report-check" [class.fm-report-check--on]="isReasonSelected(reason.id)">
                      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 12 12"
                        fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="2 6 5 9 10 3"/>
                      </svg>
                    </span>
                    <span class="fm-report-reason-name">{{ reason.name }}</span>
                  </li>
                }
              </ul>
            }

            <!-- Additional details -->
            <div class="fm-report-details-row mt-3">
              <label class="fm-report-details-label">
                Additional Details
                <span class="fm-report-optional">(Optional)</span>
              </label>
              <textarea
                class="form-control fm-report-textarea"
                [(ngModel)]="reportAdditionalDetails"
                placeholder="Provide any additional context that may help our review..."
                rows="3"
              ></textarea>
            </div>

            <!-- Submit -->
            <div class="mt-3">
              @if (reportTouched() && !selectedReportReasonIds().length) {
                <p class="fm-report-validation-msg">Please select at least one reason before submitting.</p>
              }
              <button
                type="button"
                class="fm-common-btn fm-large-btn w-100"
                [disabled]="isSubmittingReport()"
                (click)="submitReport()"
              >
                @if (isSubmittingReport()) {
                  <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                }
                Submit Report
              </button>
            </div>
        </div>
      </div>
    }<!-- /report modal -->

  </div><!-- /container-fluid -->
</div><!-- /fm-content-wrapper -->
<!-- Content Wrapper End -->

